<template>
  <div class="color-picker" v-click-outside="closeColorPicker">
    <label v-if="label" class="color-picker__label">{{ label }}</label>
    
    <div class="color-picker__container">
      <div 
        class="color-picker__trigger"
        @click="toggleColorPicker"
      >
        <div 
          class="color-picker__preview"
          :style="{ backgroundColor: value }"
        ></div>
        <input
          type="text"
          class="color-picker__input"
          :value="value"
          @input="onInputChange"
          @blur="validateInput"
          placeholder="#FFFFFF"
          maxlength="7"
        />
        <div class="color-picker__arrow" :class="{ 'color-picker__arrow--open': isOpen }">
        </div>
      </div>

      <div 
        v-if="isOpen" 
        class="color-picker__dropdown"
        @click.stop
      >
        <div 
          class="color-picker__color-area"
          ref="colorArea"
          @mousedown="startColorDrag"
          @touchstart="startColorDrag"
        >
          <div 
            class="color-picker__color-overlay"
            :style="{ backgroundColor: `hsl(${hue}, 100%, 50%)` }"
          >
            <div class="color-picker__white-overlay">
              <div class="color-picker__black-overlay">
                <div 
                  class="color-picker__cursor"
                  :style="{ 
                    left: saturation + '%', 
                    top: (100 - lightness) + '%' 
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div 
          class="color-picker__hue-slider"
          ref="hueSlider"
          @mousedown="startHueDrag"
          @touchstart="startHueDrag"
        >
          <div 
            class="color-picker__hue-cursor"
            :style="{ left: (hue / 360) * 100 + '%' }"
          ></div>
        </div>

        <div class="color-picker__presets">
          <div 
            v-for="preset in presetColors"
            :key="preset"
            class="color-picker__preset"
            :style="{ backgroundColor: preset }"
            @click="selectPreset(preset)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorPickerField',
  props: {
    value: {
      type: String,
      default: '#FFFFFF'
    },
    label: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isOpen: false,
      isDraggingColor: false,
      isDraggingHue: false,
      hue: 0,
      saturation: 0,
      lightness: 0,
      inputValue: this.value,
      presetColors: [
        '#ff0000', '#ff8000', '#ffff00', '#80ff00', '#00ff00', '#00ff80',
        '#00ffff', '#0080ff', '#0000ff', '#8000ff', '#ff00ff', '#ff0080',
        '#ffffff', '#cccccc', '#999999', '#666666', '#333333', '#000000'
      ]
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.inputValue = newVal
        this.updateHslFromHex(newVal)
      },
      immediate: true
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },
  methods: {
    toggleColorPicker() {
      this.isOpen = !this.isOpen
    },

    closeColorPicker() {
      this.isOpen = false
    },

    startColorDrag(e) {
      e.preventDefault()
      this.isDraggingColor = true
      this.updateColorFromEvent(e)
      
      document.addEventListener('mousemove', this.onColorDrag)
      document.addEventListener('mouseup', this.stopColorDrag)
      document.addEventListener('touchmove', this.onColorDrag, { passive: false })
      document.addEventListener('touchend', this.stopColorDrag)
    },

    onColorDrag(e) {
      if (!this.isDraggingColor) return
      e.preventDefault()
      this.updateColorFromEvent(e)
    },

    stopColorDrag() {
      this.isDraggingColor = false
      document.removeEventListener('mousemove', this.onColorDrag)
      document.removeEventListener('mouseup', this.stopColorDrag)
      document.removeEventListener('touchmove', this.onColorDrag)
      document.removeEventListener('touchend', this.stopColorDrag)
    },

    updateColorFromEvent(e) {
      const rect = this.$refs.colorArea.getBoundingClientRect()
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
      const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY
      
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top))
      
      this.saturation = (x / rect.width) * 100
      this.lightness = 100 - (y / rect.height) * 100
      
      this.updateColor()
    },

    startHueDrag(e) {
      e.preventDefault()
      this.isDraggingHue = true
      this.updateHueFromEvent(e)
      
      document.addEventListener('mousemove', this.onHueDrag)
      document.addEventListener('mouseup', this.stopHueDrag)
      document.addEventListener('touchmove', this.onHueDrag, { passive: false })
      document.addEventListener('touchend', this.stopHueDrag)
    },

    onHueDrag(e) {
      if (!this.isDraggingHue) return
      e.preventDefault()
      this.updateHueFromEvent(e)
    },

    stopHueDrag() {
      this.isDraggingHue = false
      document.removeEventListener('mousemove', this.onHueDrag)
      document.removeEventListener('mouseup', this.stopHueDrag)
      document.removeEventListener('touchmove', this.onHueDrag)
      document.removeEventListener('touchend', this.stopHueDrag)
    },

    updateHueFromEvent(e) {
      const rect = this.$refs.hueSlider.getBoundingClientRect()
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
      
      this.hue = (x / rect.width) * 360
      this.updateColor()
    },

    updateColor() {
      const hex = this.hslToHex(this.hue, this.saturation, this.lightness)
      this.inputValue = hex
      this.$emit('input', hex)
    },

    onInputChange(e) {
      this.inputValue = e.target.value
      if (this.isValidHex(e.target.value)) {
        this.updateHslFromHex(e.target.value)
        this.$emit('input', e.target.value)
      } else {
        this.updateHslFromHex('#FFFFFF')
      }
    },

    validateInput() {
      if (this.isValidHex(this.inputValue)) {
        this.updateHslFromHex(this.inputValue)
        this.$emit('input', this.inputValue)
      } else {
        this.inputValue = '#FFFFFF'
        this.$emit('input', this.inputValue)
      }
    },

    selectPreset(color) {
      this.inputValue = color
      this.$emit('input', color)
      this.closeColorPicker()
    },

    updateHslFromHex(hex) {
      const hsl = this.hexToHsl(hex)
      this.hue = hsl.h
      this.saturation = hsl.s
      this.lightness = hsl.l
    },

    isValidHex(hex) {
      return /^#[0-9A-Fa-f]{6}$/.test(hex)
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null
    },

    rgbToHsl(r, g, b) {
      r /= 255
      g /= 255
      b /= 255
      
      const max = Math.max(r, g, b)
      const min = Math.min(r, g, b)
      let h, s, l = (max + min) / 2
      
      if (max === min) {
        h = s = 0
      } else {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break
          case g: h = (b - r) / d + 2; break
          case b: h = (r - g) / d + 4; break
        }
        h /= 6
      }
      
      return { h: h * 360, s: s * 100, l: l * 100 }
    },

    hexToHsl(hex) {
      const rgb = this.hexToRgb(hex)
      return rgb ? this.rgbToHsl(rgb.r, rgb.g, rgb.b) : { h: 0, s: 0, l: 0 }
    },

    hslToRgb(h, s, l) {
      h = Math.max(0, Math.min(360, h)) / 360
      s = Math.max(0, Math.min(100, s)) / 100
      l = Math.max(0, Math.min(100, l)) / 100
      
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }
      
      let r, g, b
      
      if (s === 0) {
        r = g = b = l
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1/3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1/3)
      }
      
      return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
      }
    },

    hslToHex(h, s, l) {
      const rgb = this.hslToRgb(h, s, l)
      const toHex = (c) => {
        const hex = c.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }
      return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
    }
  },

  beforeDestroy() {
    if (this.isDraggingColor) {
      this.stopColorDrag()
    }
    if (this.isDraggingHue) {
      this.stopHueDrag()
    }
  }
}
</script>

<style scoped>
.color-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.color-picker__label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.color-picker__container {
  position: relative;
}

.color-picker__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s;
}

.color-picker__trigger:hover {
  border-color: #9ca3af;
}

.color-picker__preview {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.color-picker__input {
  flex: 1;
  border: none;
  outline: none;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  background: transparent;
}

.color-picker__arrow {
  font-size: 12px;
  color: #6b7280;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.color-picker__arrow--open {
  transform: rotate(180deg);
}

.color-picker__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 200px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  margin-top: 4px;
}

.color-picker__color-area {
  width: 100%;
  height: 120px;
  position: relative;
  cursor: crosshair;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.color-picker__color-overlay {
  width: 100%;
  height: 100%;
  position: relative;
}

.color-picker__white-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, #ffffff, transparent);
}

.color-picker__black-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent, #000000);
}

.color-picker__cursor {
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);
  pointer-events: none;
}

.color-picker__hue-slider {
  width: 100%;
  height: 10px;
  background: linear-gradient(to right, 
    #ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80,
    #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080, #ff0000
  );
  border-radius: 5px;
  position: relative;
  cursor: pointer;
  margin-bottom: 8px;
}

.color-picker__hue-cursor {
  position: absolute;
  top: -3px;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border: 2px solid #333333;
  border-radius: 50%;
  transform: translateX(-50%);
  pointer-events: none;
}

.color-picker__presets {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
}

.color-picker__preset {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: transform 0.1s;
}

.color-picker__preset:hover {
  transform: scale(1.1);
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .color-picker__dropdown {
    width: 180px;
  }
  
  .color-picker__color-area {
    height: 100px;
  }
  
  .color-picker__presets {
    grid-template-columns: repeat(6, 1fr);
  }
  
  .color-picker__preset {
    width: 16px;
    height: 16px;
  }
}
</style>